import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from 'react';
const DEFAULT_LANGUAGE = 'pt-BR';
const DEFAULT_DENSITY = 'comfortable';
/** Logical keys for the injectable storage backend (not necessarily localStorage). */
export const USER_PREF_LANGUAGE_KEY = 'vectorhub:preferences:language';
export const USER_PREF_DENSITY_KEY = 'vectorhub:preferences:density';
function defaultStorage() {
    if (typeof window === 'undefined' || !('localStorage' in window))
        return null;
    return window.localStorage;
}
function isAppLanguage(value) {
    return value === 'pt-BR' || value === 'en-US';
}
function isAppDensity(value) {
    return value === 'comfortable' || value === 'compact';
}
const UserPreferencesContext = createContext(null);
/**
 * Language and density preferences. Persistence is delegated to the injected
 * storage (localStorage by default; preferences service in the console).
 */
export function UserPreferencesProvider({ storage, children, }) {
    const resolvedStorage = storage === undefined ? defaultStorage() : storage;
    const [language, setLanguageState] = useState(() => {
        if (!resolvedStorage)
            return DEFAULT_LANGUAGE;
        const stored = resolvedStorage.getItem(USER_PREF_LANGUAGE_KEY);
        return isAppLanguage(stored) ? stored : DEFAULT_LANGUAGE;
    });
    const [density, setDensityState] = useState(() => {
        if (!resolvedStorage)
            return DEFAULT_DENSITY;
        const stored = resolvedStorage.getItem(USER_PREF_DENSITY_KEY);
        return isAppDensity(stored) ? stored : DEFAULT_DENSITY;
    });
    const setLanguage = useCallback((lang) => {
        setLanguageState(lang);
    }, []);
    const setDensity = useCallback((d) => {
        setDensityState(d);
    }, []);
    useEffect(() => {
        if (!resolvedStorage)
            return;
        resolvedStorage.setItem(USER_PREF_LANGUAGE_KEY, language);
    }, [resolvedStorage, language]);
    useEffect(() => {
        if (!resolvedStorage)
            return;
        resolvedStorage.setItem(USER_PREF_DENSITY_KEY, density);
    }, [resolvedStorage, density]);
    useEffect(() => {
        if (!resolvedStorage?.subscribe)
            return;
        const unsubLang = resolvedStorage.subscribe(USER_PREF_LANGUAGE_KEY, () => {
            const stored = resolvedStorage.getItem(USER_PREF_LANGUAGE_KEY);
            if (isAppLanguage(stored))
                setLanguageState(stored);
        });
        const unsubDensity = resolvedStorage.subscribe(USER_PREF_DENSITY_KEY, () => {
            const stored = resolvedStorage.getItem(USER_PREF_DENSITY_KEY);
            if (isAppDensity(stored))
                setDensityState(stored);
        });
        return () => {
            unsubLang();
            unsubDensity();
        };
    }, [resolvedStorage]);
    const value = useMemo(() => ({ language, density, setLanguage, setDensity, isLoading: false }), [language, density, setLanguage, setDensity]);
    return (_jsx(UserPreferencesContext.Provider, { value: value, children: children }));
}
export function useUserPreferences() {
    const ctx = useContext(UserPreferencesContext);
    if (!ctx) {
        throw new Error('useUserPreferences must be used within a <UserPreferencesProvider>.');
    }
    return ctx;
}
//# sourceMappingURL=UserPreferencesContext.js.map